import { useState, useEffect, useCallback, useMemo } from "react";
import { Menu } from "antd";
import { NavLink, useLocation } from "react-router-dom";
import { PushpinOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useAuthStore } from "../../stores/authStore";
import {
  useCurrentUserPrivileges,
  useAddQuickAction,
  useRemoveQuickAction,
} from "../../hooks/useUserPrivileges";
import navigationConfig from "../../config/navigationConfig.json";
import iconMap from "../../config/iconMap";

const { SubMenu } = Menu;

function Sidenav({ color }) {
  const { pathname } = useLocation();
  const { user, company } = useAuthStore();
  const userId = user?.id;

  // Determine if company is SBY (Sealanie Boat Yard) for styling
  // Use company from JWT token for accurate branding
  const isSBY = company === "sby";

  const isAdmin = user?.roles?.includes("ADMIN");
  const isSuperAdmin = user?.roles?.includes("SUPER_ADMIN");
  const { hasPrivilege, quickActions, isSystemFeatureEnabled } = useCurrentUserPrivileges();
  const { mutate: addAction } = useAddQuickAction();
  const { mutate: removeAction } = useRemoveQuickAction();

  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    item: null,
  });

  const getActiveKeys = () => {
    const keys = [];
    for (const section of navigationConfig.sections) {
      for (const item of section.items) {
        if (item.path === pathname) {
          keys.push(section.key);
          return keys;
        }
        if (item.children) {
          const hasChild = item.children.some((c) => c.path === pathname);
          if (hasChild) {
            keys.push(section.key, item.key);
            return keys;
          }
        }
      }
    }
    return keys;
  };

  const [openKeys, setOpenKeys] = useState(() => getActiveKeys());

  const onOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const closeContextMenu = useCallback(() => {
    setContextMenu((prev) => ({ ...prev, visible: false }));
  }, []);

  useEffect(() => {
    if (contextMenu.visible) {
      const handler = () => closeContextMenu();
      document.addEventListener("click", handler);
      return () => document.removeEventListener("click", handler);
    }
  }, [contextMenu.visible, closeContextMenu]);

  const handleContextMenu = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      item,
    });
  };

  const handleAddToQuickAccess = () => {
    if (contextMenu.item && userId) {
      addAction({ userId, action: contextMenu.item });
    }
    closeContextMenu();
  };

  const handleRemoveFromQuickAccess = () => {
    if (contextMenu.item && userId) {
      removeAction({ userId, path: contextMenu.item.path });
    }
    closeContextMenu();
  };

  const visibleSections = useMemo(() => {
    return navigationConfig.sections
      .filter((section) => {
        if (section.requiresRole) {
          return isAdmin;
        }
        return true;
      })
      .map((section) => {
        const filteredItems = section.items
          .map((item) => {
            if (item.children) {
              const filteredChildren = item.children.filter(
                (child) => {
                  if (child.featureKey && !isSystemFeatureEnabled(child.featureKey)) return false;
                  return isSuperAdmin || !child.privilegeKey || hasPrivilege(child.privilegeKey);
                }
              );
              if (filteredChildren.length === 0) return null;
              return { ...item, children: filteredChildren };
            }
            if (item.requiresSuperAdmin && !isSuperAdmin) return null;
            if (item.featureKey && !isSystemFeatureEnabled(item.featureKey)) return null;
            if (!isSuperAdmin && item.privilegeKey && !hasPrivilege(item.privilegeKey)) return null;
            return item;
          })
          .filter(Boolean);

        if (filteredItems.length === 0) return null;
        return { ...section, items: filteredItems };
      })
      .filter(Boolean);
  }, [isAdmin, isSuperAdmin, hasPrivilege, isSystemFeatureEnabled]);

  const isInQuickActions = useMemo(() => {
    if (!contextMenu.item) return false;
    return quickActions.some((a) => a.path === contextMenu.item.path);
  }, [contextMenu.item, quickActions]);

  return (
    <>
      {/* Brand panel: SBY has colored background, RBS uses white background */}
      <div
        className="brand"
        style={isSBY && process.env.REACT_APP_BRAND_COLOR ? { backgroundColor: process.env.REACT_APP_BRAND_COLOR, borderRadius: 8, padding: "12px 8px" } : undefined}
      >
        <img
          src={process.env.REACT_APP_LOGO_URL || "https://sealaniegroup.lk/wp-content/uploads/2022/01/group-logo-color.png"}
          alt=""
        />
        <span className="brand-name" style={isSBY && process.env.REACT_APP_BRAND_COLOR ? { color: "#fff" } : undefined}>
          {process.env.REACT_APP_COMPANY_NAME_FULL || "Royal Building Solutions (Pvt) Ltd"}
        </span>
      </div>

      <Menu
        theme="light"
        mode="inline"
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        selectedKeys={[pathname]}
      >
        {visibleSections.map((section) => (
          <SubMenu
            key={section.key}
            title={section.title}
            icon={iconMap[section.icon]}
          >
            {section.items.map((item) =>
              item.children ? (
                <SubMenu key={item.key} title={item.label}>
                  {item.children.map((child) => (
                    <Menu.Item key={child.path}>
                      <NavLink
                        to={child.path}
                        onContextMenu={(e) =>
                          handleContextMenu(e, {
                            key: child.key,
                            label: child.label,
                            path: child.path,
                          })
                        }
                      >
                        <span className="label">{child.label}</span>
                      </NavLink>
                    </Menu.Item>
                  ))}
                </SubMenu>
              ) : (
                <Menu.Item key={item.path}>
                  <NavLink
                    to={item.path}
                    onContextMenu={(e) =>
                      handleContextMenu(e, {
                        key: item.key,
                        label: item.label,
                        path: item.path,
                      })
                    }
                  >
                    <span className="label">{item.label}</span>
                  </NavLink>
                </Menu.Item>
              )
            )}
          </SubMenu>
        ))}
      </Menu>

      {contextMenu.visible && (
        <div
          className="sidenav-context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          {isInQuickActions ? (
            <div
              className="sidenav-context-menu-item"
              onClick={handleRemoveFromQuickAccess}
            >
              <CloseCircleOutlined />
              <span>Remove from Quick Access</span>
            </div>
          ) : (
            <div
              className="sidenav-context-menu-item"
              onClick={handleAddToQuickAccess}
            >
              <PushpinOutlined />
              <span>Add to Quick Access</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default Sidenav;
